/**
 * LocalStorageAdapter — browser reference implementation.
 * ---------------------------------------------------------------------
 * Zero dependencies. When window.localStorage is unavailable (Node/Jest,
 * private browsing without support), throws StorageUnavailableError on
 * every method call. Callers should probe healthCheck() before use.
 *
 * The Persistence Framework has no knowledge that this adapter uses
 * localStorage — it consumes only the contract.
 */
import { StorageUnavailableError } from '../PersistenceErrors';

const DEFAULT_PREFIX = 'giwahs-journey-';

const _readStorage = () => {
  if (typeof globalThis === 'undefined') {
    throw new StorageUnavailableError('No global scope available');
  }
  const storage = globalThis.localStorage;
  if (!storage) {
    throw new StorageUnavailableError('window.localStorage is not available');
  }
  return storage;
};

class LocalStorageAdapter {
  constructor({ prefix = DEFAULT_PREFIX } = {}) {
    this.name = 'local-storage';
    this._prefix = prefix;
  }

  _fullKey(key) {
    return key.startsWith(this._prefix) ? key : `${this._prefix}${key}`;
  }

  async get(key) {
    const storage = _readStorage();
    try {
      const value = storage.getItem(this._fullKey(key));
      return value === null ? null : String(value);
    } catch (e) {
      throw new StorageUnavailableError(`localStorage.getItem failed: ${e.message}`);
    }
  }

  async set(key, value) {
    if (typeof value !== 'string') {
      throw new StorageUnavailableError('LocalStorageAdapter.set: value must be a string');
    }
    const storage = _readStorage();
    try {
      storage.setItem(this._fullKey(key), value);
    } catch (e) {
      throw new StorageUnavailableError(`localStorage.setItem failed: ${e.message}`);
    }
  }

  async update(key, patcher) {
    const current = await this.get(key);
    const next = patcher ? patcher(current) : current;
    if (typeof next !== 'string') {
      throw new StorageUnavailableError('LocalStorageAdapter.update: patcher must return a string');
    }
    await this.set(key, next);
    return next;
  }

  async remove(key) {
    const storage = _readStorage();
    const fk = this._fullKey(key);
    const had = storage.getItem(fk) !== null;
    try {
      storage.removeItem(fk);
    } catch (e) {
      throw new StorageUnavailableError(`localStorage.removeItem failed: ${e.message}`);
    }
    return had;
  }

  async list(prefix) {
    const storage = _readStorage();
    const p = typeof prefix === 'string' ? this._fullKey(prefix) : this._prefix;
    const out = [];
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (typeof k === 'string' && k.startsWith(p)) out.push(k);
    }
    return out;
  }

  async clearExpired() {
    // See InMemoryAdapter.clearExpired — bulk expiry is a Framework
    // concern; adapters are opaque to values.
    return 0;
  }

  async healthCheck() {
    try {
      const storage = _readStorage();
      const probeKey = `${this._prefix}__health__`;
      storage.setItem(probeKey, '1');
      storage.removeItem(probeKey);
      return { ok: true, adapter: this.name, details: { prefix: this._prefix } };
    } catch (e) {
      return { ok: false, adapter: this.name, details: { message: e.message } };
    }
  }
}

export { LocalStorageAdapter, DEFAULT_PREFIX };
